import { gql, useQuery } from '@apollo/client'
import { useMemo, useRef } from 'react'

import { type DeliverySource, type DeliverySourceType, type Pagination } from '../../types'

type DeliverySourceData = Omit<DeliverySource, 'source_type'>

type DeliverySourcesQueryData = {
  sources: {
    data: DeliverySourceData[]
    pagination: Pagination
  }
}

type UseDeliverySourcesParams = {
  sourceType: DeliverySourceType
  skip?: boolean
}

const SEARCH_CAMPAIGNS = gql`
  query SearchCampaigns($page: Int!, $limit: Int!) {
    sources: mailingCampaigns(page: $page, limit: $limit) {
      data {
        id
        name
        template_code
        created_at
        updated_at
        email_count
      }
      pagination {
        current_page
        next_page
        has_next_page
        total
      }
    }
  }
`

const SEARCH_TRIGGERS = gql`
  query SearchTriggers($page: Int!, $limit: Int!) {
    sources: mailingTriggers(page: $page, limit: $limit) {
      data {
        id
        name
        template_code
        created_at
        updated_at
        email_count
      }
      pagination {
        current_page
        next_page
        has_next_page
        total
      }
    }
  }
`

const PAGE_SIZE = 20

const getOperation = (sourceType: DeliverySourceType) => {
  return sourceType === 'campaign' ? SEARCH_CAMPAIGNS : SEARCH_TRIGGERS
}

const useDeliverySources = ({ sourceType, skip = false }: UseDeliverySourcesParams) => {
  const isLoadingMore = useRef(false)
  const operation = getOperation(sourceType)
  const variables = useMemo(
    () => ({
      page: 1,
      limit: PAGE_SIZE
    }),
    []
  )
  const { data, error, fetchMore, loading, networkStatus } = useQuery<DeliverySourcesQueryData>(operation, {
    variables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip
  })
  const sourceData = data?.sources.data ?? []
  const items = useMemo<DeliverySource[]>(() => {
    return sourceData.map(source => ({
      ...source,
      source_type: sourceType
    }))
  }, [sourceData, sourceType])

  const fetchNextPage = async () => {
    if (networkStatus === 3 || isLoadingMore.current) {
      return
    }

    const nextPage = data?.sources.pagination.next_page

    if (!nextPage) {
      return
    }

    try {
      isLoadingMore.current = true

      await fetchMore({
        variables: {
          page: nextPage,
          limit: PAGE_SIZE
        }
      })
    } catch (fetchError) {
      console.error('Error fetching the next delivery sources page:', fetchError)
    } finally {
      isLoadingMore.current = false
    }
  }

  return {
    error,
    fetchNextPage,
    items,
    loading: (loading && networkStatus !== 3) || networkStatus === 2,
    loadingNext: networkStatus === 3,
    shouldFetchNextPage: Boolean(!loading && !isLoadingMore.current && data?.sources.pagination.has_next_page),
    total: data?.sources.pagination.total ?? 0
  }
}

export default useDeliverySources
